import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../api-config';

export interface RepoSelectOption {
  id: string;
  text: string;
  /** URL completa de entrada a la app (origen + path). */
  code: string;
}

/**
 * Catálogo de sistemas/repos para el selector del header.
 * GET {apiUrl}/selects/repo-selects
 * → { success: true, data: [{ id, code, text }] }
 */
@Injectable({ providedIn: 'root' })
export class RepoSelectsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, RepoSelectOption[]>();
  private readonly endpointUrl = '/selects/repo-selects';

  getOptions(): Observable<RepoSelectOption[]> {
    const cached = this.cache.get(this.endpointUrl);
    if (cached) {
      return of(cached);
    }

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(this.endpointUrl, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(body: unknown): RepoSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id?: string; code?: string; text?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data
        .map((item) => ({
          id: item.id || '',
          text: (item.text || '').trim(),
          code: (item.code || '').trim(),
        }))
        .filter((item) => item.text && item.code);
    } catch {
      return [];
    }
  }
}

/** Separa `code` (URL completa) en origen + path para el handoff SSO. */
export function splitRepoCodeUrl(
  code: string,
): { baseUrl: string; entryPath: string } | null {
  try {
    const url = new URL(code);
    const path = `${url.pathname}${url.search}` || '/app';
    return {
      baseUrl: url.origin,
      entryPath: path,
    };
  } catch {
    return null;
  }
}
